import React from 'react'
import {
  RefreshCcw,
  Repeat,
  CalendarClock,
  BellRing,
  IndianRupee,
  TrendingUp,
  AlertTriangle,
  GitBranch
} from 'lucide-react'
import contractReportsService from '../../../services/contractReportsService'
import {
  ReportShell,
  ReportSection,
  ReportTable,
  KpiGrid,
  EmptyState,
  StatusBadge,
  ContractLink,
  useContractReport,
  toInputDate,
  formatMoney,
  formatMoneyCompact,
  formatNumber,
  formatPercent,
  formatDate,
  primaryCurrency,
  contractColumns,
  withExportOnly,
  breakdownColumns,
  breakdownFooter,
  summarySection
} from './reportShared'

const DEFAULT_FILTERS = { asOf: toInputDate(), fromDate: '', toDate: '' }

const yesNo = (v) => (v ? 'Yes' : 'No')

const upcomingCols = withExportOnly(
  [
    contractColumns.contract,
    contractColumns.party,
    contractColumns.value,
    contractColumns.end,
    {
      key: 'daysToExpiry',
      label: 'Days Left',
      align: 'right',
      render: (r) => (
        <StatusBadge status={`${r.daysToExpiry}d`} tone={r.daysToExpiry <= 30 ? 'danger' : r.daysToExpiry <= 60 ? 'warning' : 'info'} />
      ),
      exportValue: (r) => r.daysToExpiry
    },
    {
      key: 'isRenewable',
      label: 'Renewable',
      render: (r) => (r.isRenewable ? (r.autoRenewal ? 'Auto' : 'Yes') : 'No'),
      exportValue: (r) => yesNo(r.isRenewable)
    },
    {
      key: 'noticeDeadline',
      label: 'Notice By',
      render: (r) =>
        r.isRenewable ? (
          <span style={{ color: r.noticeOverdue ? '#b91c1c' : undefined }}>
            {formatDate(r.noticeDeadline)}
            <span className="contract-report-subtext">{r.renewalNoticePeriod}d notice{r.noticeOverdue ? ' · passed' : ''}</span>
          </span>
        ) : '—',
      exportValue: (r) => (r.isRenewable ? formatDate(r.noticeDeadline) : '')
    },
    {
      key: 'hasRenewalInProgress',
      label: 'Renewal Record',
      render: (r) => (r.hasRenewalInProgress ? <StatusBadge status="In progress" tone="success" /> : <StatusBadge status="None" tone="warning" />),
      exportValue: (r) => yesNo(r.hasRenewalInProgress)
    }
  ],
  [contractColumns.title, contractColumns.currency, { key: 'autoRenewal', label: 'Auto Renewal', exportValue: (r) => yesNo(r.autoRenewal) }]
)

const recentCols = [
  {
    key: 'renewalNumber',
    label: 'Renewal',
    render: (r) => (
      <div>
        {r.renewalNumber}
        <span className="contract-report-subtext">{r.renewalType}{r.isAutoRenewal ? ' · auto' : ''}</span>
      </div>
    )
  },
  {
    key: 'originalContractNumber',
    label: 'Original Contract',
    render: (r) => <ContractLink id={r.originalContractId} number={r.originalContractNumber} title={r.originalContractTitle} />
  },
  { key: 'partyName', label: 'Party' },
  { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  { key: 'renewalDate', label: 'Renewal Date', render: (r) => formatDate(r.renewalDate), exportValue: (r) => formatDate(r.renewalDate) },
  {
    key: 'renewalStartDate',
    label: 'New Term',
    render: (r) => `${formatDate(r.renewalStartDate)} – ${formatDate(r.renewalEndDate)}`,
    exportValue: (r) => `${formatDate(r.renewalStartDate)} - ${formatDate(r.renewalEndDate)}`
  },
  {
    key: 'renewalValue',
    label: 'Value',
    align: 'right',
    render: (r) => formatMoney(r.renewalValue, r.currency),
    exportValue: (r) => r.renewalValue
  }
]

const recentExportCols = [
  ...recentCols.slice(0, 1),
  { key: 'renewalType', label: 'Renewal Type' },
  ...recentCols.slice(1, 2),
  { key: 'originalContractTitle', label: 'Original Title' },
  ...recentCols.slice(2),
  { key: 'currency', label: 'Currency' }
].map((c) => (c.key === 'originalContractNumber' ? { ...c, exportValue: (r) => r.originalContractNumber } : c))

const ContractRenewalReport = () => {
  const report = useContractReport(contractReportsService.getRenewalReport, DEFAULT_FILTERS)

  const getExportSections = (data) => {
    const cur = primaryCurrency(data.meta)
    const s = data.summary
    return [
      summarySection('Summary', [
        ['Renewable active contracts', s.renewableActive],
        ['Auto-renewal on', s.autoRenewalOn],
        ['Expiring in 30 days', s.expiring30],
        ['Expiring in 60 days', s.expiring60],
        ['Expiring in 90 days', s.expiring90],
        ['Renewal notice due soon', s.noticeDueSoon],
        ['Expiring (90d) without renewal record', s.expiringWithoutRenewal],
        ['Renewal records', data.pipeline.totalRenewals],
        ['Total renewal value', data.pipeline.totalRenewalValue],
        ['Conversion rate (%)', data.conversion.available ? data.conversion.conversionRate : 'Unavailable'],
        ['Conversion note', data.conversion.note]
      ]),
      { title: 'Pipeline by Status', columns: breakdownColumns('status', 'Status', data.pipeline.byStatus, cur), rows: data.pipeline.byStatus },
      { title: 'Pipeline by Type', columns: breakdownColumns('renewalType', 'Renewal Type', data.pipeline.byType, cur), rows: data.pipeline.byType },
      { title: 'Upcoming Renewals', columns: upcomingCols.export, rows: data.upcomingRenewals },
      { title: 'Recent Renewals', columns: recentExportCols, rows: data.recentRenewals }
    ]
  }

  return (
    <ReportShell
      title="Renewal Reports"
      subtitle="Contract renewal pipeline and expiration tracking"
      path="/contracts/reports/renewals"
      report={report}
      filterFields={['asOf', 'fromDate', 'toDate']}
      filterHint="Expiry windows are measured from the as-of date; period filters renewal records by renewal date"
      exportName="Contract_Renewals"
      getExportSections={getExportSections}
    >
      {(data) => {
        const s = data.summary
        const p = data.pipeline
        const c = data.conversion
        const cur = primaryCurrency(data.meta)
        if (p.totalRenewals === 0 && data.upcomingRenewals.length === 0 && s.renewableActive === 0 && !c.available) {
          return <EmptyState message="No renewal activity or upcoming expiries found" />
        }
        return (
          <>
            <KpiGrid
              items={[
                {
                  icon: Repeat,
                  label: 'Renewable Active',
                  value: formatNumber(s.renewableActive),
                  subtitle: `${formatNumber(s.autoRenewalOn)} with auto-renewal`
                },
                {
                  icon: CalendarClock,
                  label: 'Expiring in 30 Days',
                  value: formatNumber(s.expiring30),
                  badge: `60d: ${s.expiring60} · 90d: ${s.expiring90}`,
                  badgeTone: s.expiring30 > 0 ? 'negative' : 'neutral',
                  subtitle: 'Active contracts'
                },
                {
                  icon: BellRing,
                  label: 'Notice Due Soon',
                  value: formatNumber(s.noticeDueSoon),
                  subtitle: 'Notice deadline within 30 days',
                  title: data.meta.noticeRule
                },
                {
                  icon: AlertTriangle,
                  label: 'Expiring, No Renewal',
                  value: formatNumber(s.expiringWithoutRenewal),
                  subtitle: 'Ending in 90d without a renewal record'
                }
              ]}
            />
            <KpiGrid
              items={[
                {
                  icon: GitBranch,
                  label: 'Renewal Records',
                  value: formatNumber(p.totalRenewals),
                  subtitle: 'In selected period'
                },
                {
                  icon: IndianRupee,
                  label: 'Renewal Value',
                  value: formatMoneyCompact(p.totalRenewalValue, cur),
                  title: formatMoney(p.totalRenewalValue, cur),
                  subtitle: 'Sum of renewal values'
                },
                {
                  icon: TrendingUp,
                  label: 'Renewal Conversion',
                  value: c.available ? formatPercent(c.conversionRate) : 'N/A',
                  subtitle: c.available
                    ? `${formatNumber(c.renewedContracts)} renewed · ${formatNumber(c.expiredWithoutRenewal)} lapsed`
                    : 'Not enough history',
                  title: c.note
                },
                {
                  icon: RefreshCcw,
                  label: 'Auto-Renewal On',
                  value: formatNumber(s.autoRenewalOn),
                  subtitle: 'Active contracts'
                }
              ]}
            />

            <div className="contract-report-grid-2">
              <ReportSection title="Renewal Pipeline by Status" count={`${p.totalRenewals} records`}>
                <ReportTable
                  columns={breakdownColumns('status', 'Status', p.byStatus, cur, { badge: true })}
                  rows={p.byStatus}
                  footer={breakdownFooter('status', p.byStatus, cur)}
                  emptyText="No renewal records"
                />
              </ReportSection>
              <ReportSection title="Renewal Pipeline by Type">
                <ReportTable
                  columns={breakdownColumns('renewalType', 'Renewal Type', p.byType, cur)}
                  rows={p.byType}
                  footer={breakdownFooter('renewalType', p.byType, cur)}
                  emptyText="No renewal records"
                />
              </ReportSection>
            </div>

            <ReportSection
              title="Upcoming Expiries (next 90 days)"
              subtitle="Active signed contracts sorted by end date"
              count={`${data.upcomingRenewals.length} contracts`}
            >
              <ReportTable columns={upcomingCols.display} rows={data.upcomingRenewals} emptyText="No active contracts end within 90 days" />
            </ReportSection>

            <ReportSection title="Recent Renewals" subtitle="Latest 20 renewal records" count={`${data.recentRenewals.length} shown`}>
              <ReportTable columns={recentCols} rows={data.recentRenewals} emptyText="No renewal records" />
            </ReportSection>

            {!c.available && (
              <div className="contract-report-note info">
                <span>Conversion: {c.note}</span>
              </div>
            )}
          </>
        )
      }}
    </ReportShell>
  )
}

export default ContractRenewalReport
